import { useState } from "react";
import { Bell } from "lucide-react";
import { AppPopover } from "@/components/app/AppPopover";
import { Typography } from "@/components/app/Typography";
import { cn } from "@/lib/utils";
import {
  useNotifications,
  useMarkNotificationRead,
} from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/lib/routes";

export function NotificationPopover() {
  const [tab, setTab] = useState<"all" | "unread">("all");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const { data: notificationsResponse } = useNotifications({
    limit: 50,
  });

  const { mutate: markRead } = useMarkNotificationRead();

  const notifications = notificationsResponse?.data?.items || [];
  const unreadCount = notificationsResponse?.data?.unreadCount || 0;

  const filtered =
    tab === "unread"
      ? notifications.filter((n: any) => !n.isRead)
      : notifications;

  const trigger = (
    <button
      aria-label="Notifications"
      className="relative flex items-center justify-center h-10 w-10 rounded-full hover:bg-accent transition-colors text-[#887771]"
    >
      <Bell size={24} />
      {unreadCount > 0 && (
        <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-destructive" />
      )}
    </button>
  );

  return (
    <AppPopover
      trigger={trigger}
      overlay
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border-light">
        <Typography variant="h4" color="primary">
          Notifications
        </Typography>
        <div className="flex items-center rounded-full p-1 gap-1 border border-border-light">
          {(["all", "unread"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "px-5 py-1.5 rounded-full text-base font-medium transition-colors capitalize md:h-[52px] md:min-w-[107px] flex justify-center items-center",
                tab === t
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t === "all" ? "All" : "Unread"}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="max-h-[420px] overflow-y-auto divide-y divide-border-light scrollbar-thin md:w-[450px]">
        {filtered.length === 0 ? (
          <div className="py-10 text-center">
            <Typography variant="body2" color="muted">
              No notifications
            </Typography>
          </div>
        ) : (
          filtered.map((n: any) => (
            <div
              key={n.id}
              className="p-3 md:p-5 hover:bg-muted/40 transition-colors cursor-pointer"
              onClick={() => {
                if (!n.isRead) markRead(n.id);
                if (n.referenceId) {
                  setIsOpen(false);
                  navigate(ROUTES.DASHBOARD.ORDER_VIEW(String(n.referenceId)));
                }
              }}
            >
              <div className="relative flex items-start gap-3">
                <div className="h-11 md:w-[59px] w-11 md:h-[59px] rounded-full overflow-hidden shrink-0 bg-muted flex items-center justify-center">
                  {n.imageUrl ? (
                    <img
                      src={n.imageUrl}
                      alt={n.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Bell className="text-muted-foreground" size={24} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Typography className="font-bold">{n.title}</Typography>
                    <Typography variant="caption" color="muted">
                      {formatDistanceToNow(new Date(n.createdAt), {
                        addSuffix: true,
                      })}
                    </Typography>
                  </div>
                  <Typography
                    variant="caption"
                    color="muted"
                    className="line-clamp-2 mt-0.5"
                  >
                    {n.message}
                  </Typography>
                </div>
                {!n.isRead && (
                  <span className="absolute top-2 right-0 h-2.5 w-2.5 rounded-full bg-success" />
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </AppPopover>
  );
}
